import jwtDecode from 'jwt-decode';

// ----------------------------------------------------------------------
// Phase 2.1 session-aware JWT helpers.
//
// Storage layout (Phase 2 — moves to httpOnly cookie in Phase 4):
//   localStorage.accessToken   — short-lived JWT used as Bearer header
//   localStorage.refreshToken  — long-lived refresh JWT (admin: 8h)
//
// The strategy file `axios.ts` owns the 401-and-refresh interceptor. The
// helpers here are pure: token persistence, expiry math, axios default
// header sync. They do NOT call the API directly — that's the
// interceptor's job — so they can be safely called from anywhere
// (login, logout, refresh) without circular-import risk.
// ----------------------------------------------------------------------

const ACCESS_KEY = 'accessToken';
const REFRESH_KEY = 'refreshToken';

export const isValidToken = (accessToken: string | null | undefined): boolean => {
  if (!accessToken) return false;
  try {
    const decoded = jwtDecode<{ exp: number }>(accessToken);
    const currentTime = Date.now() / 1000;
    return decoded.exp > currentTime;
  } catch {
    return false;
  }
};

export const getAccessToken = (): string | null =>
  localStorage.getItem(ACCESS_KEY);

export const getRefreshToken = (): string | null =>
  localStorage.getItem(REFRESH_KEY);

export const getTokenExpiry = (token: string | null | undefined): number | null => {
  if (!token) return null;
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);
    return exp;
  } catch {
    return null;
  }
};

/**
 * Persist the current access token + (optional) refresh token, sync the
 * axios `Authorization` default. Passing nulls clears everything.
 */
export const setSession = (
  accessToken: string | null,
  refreshToken?: string | null,
): void => {
  // Lazy require to avoid a circular import with utils/axios.
  // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
  const axiosInstance = require('./axios').default;

  if (accessToken) {
    localStorage.setItem(ACCESS_KEY, accessToken);
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem(ACCESS_KEY);
    delete axiosInstance.defaults.headers.common.Authorization;
  }

  // refreshToken === undefined → don't touch existing value.
  // refreshToken === null      → clear it.
  // refreshToken === '...'     → write it.
  if (refreshToken !== undefined) {
    if (refreshToken) {
      localStorage.setItem(REFRESH_KEY, refreshToken);
    } else {
      localStorage.removeItem(REFRESH_KEY);
    }
  }
};

export const clearSession = (): void => {
  setSession(null, null);
};
